import { db } from '../config/firebase';
import {
  collection, doc, getDocs, getDoc, setDoc, addDoc, updateDoc, deleteDoc,
  query, where, orderBy, serverTimestamp, onSnapshot
} from 'firebase/firestore';

// ==== FIELD MAPPING LAYER ====
const STATUS_FROM_DB = { pending: 'ingediend', approved: 'goedgekeurd', rejected: 'afgekeurd', ingetrokken: 'ingetrokken' };
const STATUS_TO_DB = { ingediend: 'pending', goedgekeurd: 'approved', afgekeurd: 'rejected', ingetrokken: 'ingetrokken' };

function mapUserFromDB(data) {
  // Backward compat: old rol 'admin' → isAdmin, old rol 'manager' → isManager
  const oldRol = data.role || 'medewerker';
  const isAdminFlag = data.isAdmin ?? (oldRol === 'admin');
  const isManagerFlag = data.isManager ?? (oldRol === 'manager');
  const baseRol = (oldRol === 'admin' || oldRol === 'manager') ? 'medewerker' : oldRol;

  return {
    naam: data.name || '',
    email: data.email || '',
    wachtwoord: data.password || '',
    rol: baseRol,
    isManager: isManagerFlag,
    isAdmin: isAdminFlag,
    status: data.status || 'actief',
    uitDienstDatum: data.uitDienstDatum || null,
    afdeling: data.department || '',
    contractUren: data.contractUren ?? 200,
    meegenomenUren: data.meegenomenUren ?? 0,
    meegenomenUrenJaar: data.meegenomenUrenJaar || null,
    indienstDatum: data.indienstDatum || null,
    contractWijzigingen: data.contractWijzigingen || [],
    flexEnabled: data.flexEnabled ?? false,
    weekFlexUren: data.weekFlexUren ?? 0,
    werkrooster: data.werkrooster || null,
    notificatieEmail: data.notificatieEmail ?? true,
    notificatieApp: data.notificatieApp ?? true,
    telefoon: data.telefoon || '',
    heeftFlexProfiel: data.heeftFlexProfiel ?? false,
    showroomMedewerker: data.showroomMedewerker ?? false,
    verkorteNaam: data.verkorteNaam || '',
    kleur: data.kleur || '',
    mustChangePassword: data.mustChangePassword ?? false,
    zaterdagDrempel: data.zaterdagDrempel ?? 0,
  };
}

function mapUserToDB(data) {
  const mapped = {};
  if (data.naam !== undefined) mapped.name = data.naam;
  if (data.email !== undefined) mapped.email = data.email;
  if (data.wachtwoord !== undefined) mapped.password = data.wachtwoord;
  if (data.rol !== undefined) mapped.role = data.rol;
  if (data.isManager !== undefined) mapped.isManager = data.isManager;
  if (data.isAdmin !== undefined) mapped.isAdmin = data.isAdmin;
  if (data.status !== undefined) mapped.status = data.status;
  if (data.uitDienstDatum !== undefined) mapped.uitDienstDatum = data.uitDienstDatum;
  if (data.afdeling !== undefined) mapped.department = data.afdeling;
  if (data.contractUren !== undefined) mapped.contractUren = data.contractUren;
  if (data.meegenomenUren !== undefined) mapped.meegenomenUren = data.meegenomenUren;
  if (data.meegenomenUrenJaar !== undefined) mapped.meegenomenUrenJaar = data.meegenomenUrenJaar;
  if (data.flexEnabled !== undefined) mapped.flexEnabled = data.flexEnabled;
  if (data.weekFlexUren !== undefined) mapped.weekFlexUren = data.weekFlexUren;
  if (data.indienstDatum !== undefined) mapped.indienstDatum = data.indienstDatum;
  if (data.contractWijzigingen !== undefined) mapped.contractWijzigingen = data.contractWijzigingen;
  if (data.werkrooster !== undefined) mapped.werkrooster = data.werkrooster;
  if (data.notificatieEmail !== undefined) mapped.notificatieEmail = data.notificatieEmail;
  if (data.notificatieApp !== undefined) mapped.notificatieApp = data.notificatieApp;
  if (data.telefoon !== undefined) mapped.telefoon = data.telefoon;
  if (data.heeftFlexProfiel !== undefined) mapped.heeftFlexProfiel = data.heeftFlexProfiel;
  if (data.showroomMedewerker !== undefined) mapped.showroomMedewerker = data.showroomMedewerker;
  if (data.verkorteNaam !== undefined) mapped.verkorteNaam = data.verkorteNaam;
  if (data.kleur !== undefined) mapped.kleur = data.kleur;
  if (data.mustChangePassword !== undefined) mapped.mustChangePassword = data.mustChangePassword;
  if (data.zaterdagDrempel !== undefined) mapped.zaterdagDrempel = data.zaterdagDrempel;
  return mapped;
}

function mapLeaveFromDB(data) {
  return {
    medewerkerId: data.userId || '',
    medewerkerNaam: data.userName || '',
    ingediendDoor: data.submittedBy || '',
    ingediendDoorNaam: data.submittedByName || '',
    type: data.type || '',
    startDatum: data.startDate || '',
    eindDatum: data.endDate || '',
    uren: data.uren ?? data.days ?? 0,
    reden: data.reason || '',
    status: STATUS_FROM_DB[data.status] || data.status || 'ingediend',
    jaar: data.jaar || new Date().getFullYear(),
    gedeeltelijk: data.gedeeltelijk ?? false,
    beginTijd: data.beginTijd || null,
    eindTijd: data.eindTijd || null,
    isOpenZiek: data.isOpenZiek ?? false,
    gebruikOveruren: data.gebruikOveruren ?? false,
    overurenGebruikt: data.overurenGebruikt ?? 0,
    calendarEventId: data.calendarEventId || null,
    createdAt: data.createdAt || null,
  };
}

function mapLeaveToDB(data) {
  const mapped = {};
  if (data.medewerkerId !== undefined) mapped.userId = data.medewerkerId;
  if (data.medewerkerNaam !== undefined) mapped.userName = data.medewerkerNaam;
  if (data.ingediendDoor !== undefined) mapped.submittedBy = data.ingediendDoor;
  if (data.ingediendDoorNaam !== undefined) mapped.submittedByName = data.ingediendDoorNaam;
  if (data.type !== undefined) mapped.type = data.type;
  if (data.startDatum !== undefined) mapped.startDate = data.startDatum;
  if (data.eindDatum !== undefined) mapped.endDate = data.eindDatum;
  if (data.uren !== undefined) mapped.uren = data.uren;
  if (data.reden !== undefined) mapped.reason = data.reden;
  if (data.status !== undefined) mapped.status = STATUS_TO_DB[data.status] || data.status;
  if (data.jaar !== undefined) mapped.jaar = data.jaar;
  if (data.gedeeltelijk !== undefined) mapped.gedeeltelijk = data.gedeeltelijk;
  if (data.beginTijd !== undefined) mapped.beginTijd = data.beginTijd;
  if (data.eindTijd !== undefined) mapped.eindTijd = data.eindTijd;
  if (data.isOpenZiek !== undefined) mapped.isOpenZiek = data.isOpenZiek;
  if (data.gebruikOveruren !== undefined) mapped.gebruikOveruren = data.gebruikOveruren;
  if (data.overurenGebruikt !== undefined) mapped.overurenGebruikt = data.overurenGebruikt;
  if (data.calendarEventId !== undefined) mapped.calendarEventId = data.calendarEventId;
  return mapped;
}

function mapHolidayFromDB(data) {
  return { datum: data.date || '', naam: data.name || '' };
}

function mapHolidayToDB(data) {
  const mapped = {};
  if (data.datum !== undefined) mapped.date = data.datum;
  if (data.naam !== undefined) mapped.name = data.naam;
  return mapped;
}

// ==== MEDEWERKERS (Firestore: 'users') ====
export function subscribeMedewerkers(callback) {
  return onSnapshot(collection(db, 'users'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...mapUserFromDB(d.data()) })));
  });
}

export async function addMedewerker(medewerker) {
  const ref = await addDoc(collection(db, 'users'), {
    ...mapUserToDB(medewerker),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateMedewerker(id, updates) {
  await updateDoc(doc(db, 'users', id), mapUserToDB(updates));
}

export async function deleteMedewerker(id) {
  await deleteDoc(doc(db, 'users', id));
}

/**
 * Verwijder medewerker met ALLE bijbehorende gegevens
 * (verlofaanvragen, overuren, roosterwijzigingen, flex data, archief)
 */
export async function deleteMedewerkerCompleet(id) {
  // Delete verlofaanvragen
  const verlofSnap = await getDocs(query(collection(db, 'leaveRequests'), where('userId', '==', id)));
  for (const d of verlofSnap.docs) await deleteDoc(d.ref);

  // Delete overuren
  const ouSnap = await getDocs(query(collection(db, 'overuren'), where('userId', '==', id)));
  for (const d of ouSnap.docs) await deleteDoc(d.ref);

  // Delete roosterwijzigingen
  const rwSnap = await getDocs(query(collection(db, 'roosterWijzigingen'), where('medewerkerId', '==', id)));
  for (const d of rwSnap.docs) await deleteDoc(d.ref);

  // Delete jaararchief
  const jaSnap = await getDocs(query(collection(db, 'jaararchief'), where('medewerkerId', '==', id)));
  for (const d of jaSnap.docs) await deleteDoc(d.ref);

  // Delete flex planning
  const fpSnap = await getDocs(query(collection(db, 'flexPlanning'), where('flexkrachtId', '==', id)));
  for (const d of fpSnap.docs) await deleteDoc(d.ref);

  // Delete flex uren
  const fuSnap = await getDocs(query(collection(db, 'flexUren'), where('flexkrachtId', '==', id)));
  for (const d of fuSnap.docs) await deleteDoc(d.ref);

  // Delete flex uitbetalingen
  const fubSnap = await getDocs(query(collection(db, 'flexUitbetalingen'), where('flexkrachtId', '==', id)));
  for (const d of fubSnap.docs) await deleteDoc(d.ref);

  // Delete medewerker record
  await deleteDoc(doc(db, 'users', id));
}

/**
 * Reset alle mutaties van een medewerker maar BEHOUD het medewerker profiel.
 * Verwijdert: verlofaanvragen, overuren, roosterwijzigingen, jaararchief,
 * flex planning, flex uren, flex uitbetalingen, notificaties.
 * Retourneert stats met aantallen verwijderde records.
 */
export async function resetMedewerkerMutaties(id) {
  const stats = { verlof: 0, overuren: 0, archief: 0, flex: 0, notificaties: 0 };

  // Delete verlofaanvragen
  const verlofSnap = await getDocs(query(collection(db, 'leaveRequests'), where('userId', '==', id)));
  for (const d of verlofSnap.docs) await deleteDoc(d.ref);
  stats.verlof = verlofSnap.size;

  // Delete overuren
  const ouSnap = await getDocs(query(collection(db, 'overuren'), where('userId', '==', id)));
  for (const d of ouSnap.docs) await deleteDoc(d.ref);
  stats.overuren = ouSnap.size;

  // NOTE: Roosterwijzigingen worden NIET verwijderd — het werkrooster moet behouden blijven

  // Delete jaararchief
  const jaSnap = await getDocs(query(collection(db, 'jaararchief'), where('medewerkerId', '==', id)));
  for (const d of jaSnap.docs) await deleteDoc(d.ref);
  stats.archief = jaSnap.size;

  // Delete flex planning
  const fpSnap = await getDocs(query(collection(db, 'flexPlanning'), where('flexkrachtId', '==', id)));
  for (const d of fpSnap.docs) await deleteDoc(d.ref);

  // Delete flex uren
  const fuSnap = await getDocs(query(collection(db, 'flexUren'), where('flexkrachtId', '==', id)));
  for (const d of fuSnap.docs) await deleteDoc(d.ref);

  // Delete flex uitbetalingen
  const fubSnap = await getDocs(query(collection(db, 'flexUitbetalingen'), where('flexkrachtId', '==', id)));
  for (const d of fubSnap.docs) await deleteDoc(d.ref);
  stats.flex = fpSnap.size + fuSnap.size + fubSnap.size;

  // Delete notificaties voor deze medewerker
  try {
    const notSnap = await getDocs(query(collection(db, 'notificaties'), where('userId', '==', id)));
    for (const d of notSnap.docs) await deleteDoc(d.ref);
    stats.notificaties = notSnap.size;
  } catch { /* notificaties collectie bestaat misschien niet */ }

  return stats;
}

// ==== VERLOFAANVRAGEN (Firestore: 'leaveRequests') ====
export function subscribeVerlofaanvragen(callback) {
  return onSnapshot(collection(db, 'leaveRequests'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...mapLeaveFromDB(d.data()) })));
  });
}

export async function addVerlofaanvraag(aanvraag) {
  const ref = await addDoc(collection(db, 'leaveRequests'), {
    ...mapLeaveToDB(aanvraag),
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateVerlofaanvraag(id, updates) {
  await updateDoc(doc(db, 'leaveRequests', id), mapLeaveToDB(updates));
}

// ==== OVERUREN (Firestore: 'overuren') ====
export function subscribeOveruren(callback) {
  return onSnapshot(collection(db, 'overuren'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addOveruren(record) {
  const ref = await addDoc(collection(db, 'overuren'), {
    ...record,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateOveruren(id, updates) {
  await updateDoc(doc(db, 'overuren', id), updates);
}

// ==== FEESTDAGEN (Firestore: 'holidays') ====
export function subscribeFeestdagen(callback) {
  return onSnapshot(collection(db, 'holidays'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...mapHolidayFromDB(d.data()) })));
  });
}

export async function addFeestdag(feestdag) {
  const ref = await addDoc(collection(db, 'holidays'), mapHolidayToDB(feestdag));
  return ref.id;
}

export async function deleteFeestdag(id) {
  await deleteDoc(doc(db, 'holidays', id));
}

// ==== VAKANTIEPERIODEN (Firestore: 'vakantiePerioden') ====
export function subscribeVakantiePerioden(callback) {
  return onSnapshot(collection(db, 'vakantiePerioden'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addVakantiePeriode(periode) {
  const ref = await addDoc(collection(db, 'vakantiePerioden'), {
    naam: periode.naam,
    startDatum: periode.startDatum,
    eindDatum: periode.eindDatum,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateVakantiePeriode(id, updates) {
  await updateDoc(doc(db, 'vakantiePerioden', id), updates);
}

export async function deleteVakantiePeriode(id) {
  await deleteDoc(doc(db, 'vakantiePerioden', id));
}

// ==== ROOSTERWIJZIGINGEN (Firestore: 'roosterWijzigingen') ====
export function subscribeRoosterWijzigingen(callback) {
  return onSnapshot(collection(db, 'roosterWijzigingen'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addRoosterWijziging(wijziging) {
  const ref = await addDoc(collection(db, 'roosterWijzigingen'), {
    ...wijziging,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function deleteRoosterWijziging(id) {
  await deleteDoc(doc(db, 'roosterWijzigingen', id));
}

// ==== JAARARCHIEF (Firestore: 'jaararchief') ====
export function subscribeJaararchief(callback) {
  return onSnapshot(collection(db, 'jaararchief'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addJaararchief(record) {
  const ref = await addDoc(collection(db, 'jaararchief'), record);
  return ref.id;
}

// ==== FLEX PLANNING (Firestore: 'flexPlanning') ====
export function subscribeFlexPlanning(callback) {
  return onSnapshot(collection(db, 'flexPlanning'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addFlexPlanning(planning) {
  const ref = await addDoc(collection(db, 'flexPlanning'), {
    ...planning,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateFlexPlanning(id, updates) {
  await updateDoc(doc(db, 'flexPlanning', id), updates);
}

export async function deleteFlexPlanning(id) {
  await deleteDoc(doc(db, 'flexPlanning', id));
}

// ==== FLEX UREN (Firestore: 'flexUren') ====
export function subscribeFlexUren(callback) {
  return onSnapshot(collection(db, 'flexUren'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addFlexUren(record) {
  const ref = await addDoc(collection(db, 'flexUren'), {
    ...record,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateFlexUren(id, updates) {
  await updateDoc(doc(db, 'flexUren', id), updates);
}

// ==== FLEX UITBETALINGEN (Firestore: 'flexUitbetalingen') ====
export function subscribeFlexUitbetalingen(callback) {
  return onSnapshot(collection(db, 'flexUitbetalingen'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addFlexUitbetaling(record) {
  const ref = await addDoc(collection(db, 'flexUitbetalingen'), {
    ...record,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ==== VERLOFTYPEN (Firestore: 'verlofTypes') ====
export function subscribeVerlofTypes(callback) {
  return onSnapshot(collection(db, 'verlofTypes'), (snapshot) => {
    callback(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addVerlofType(data) {
  const ref = await addDoc(collection(db, 'verlofTypes'), {
    key: data.key,
    text: data.text,
    label: data.label,
    color: data.color || '#767676',
    trektAfVanVerlof: data.trektAfVanVerlof ?? true,
    aftrekSaldo: data.aftrekSaldo || 'verlof',
    sortOrder: data.sortOrder || 99,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateVerlofType(id, updates) {
  await updateDoc(doc(db, 'verlofTypes', id), updates);
}

export async function deleteVerlofType(id) {
  await deleteDoc(doc(db, 'verlofTypes', id));
}

// ==== MENU CONFIGURATIE (Firestore: 'settings/menuConfig') ====
export function subscribeMenuConfig(callback) {
  return onSnapshot(doc(db, 'settings', 'menuConfig'), (snapshot) => {
    callback(snapshot.exists() ? snapshot.data().items || [] : []);
  });
}

export async function saveMenuConfig(items) {
  await setDoc(doc(db, 'settings', 'menuConfig'), { items, updatedAt: serverTimestamp() });
}

// ==== BATCH IMPORT ====
export async function batchImportVerlof(records) {
  const results = { success: 0, errors: [] };
  for (const r of records) {
    try {
      await addDoc(collection(db, 'leaveRequests'), {
        ...mapLeaveToDB(r),
        createdAt: serverTimestamp(),
      });
      results.success++;
    } catch (err) {
      results.errors.push(`${r.medewerkerNaam} ${r.startDatum}: ${err.message}`);
    }
  }
  return results;
}

export async function batchImportOveruren(records) {
  const results = { success: 0, errors: [] };
  for (const r of records) {
    try {
      await addDoc(collection(db, 'overuren'), {
        ...r,
        createdAt: serverTimestamp(),
      });
      results.success++;
    } catch (err) {
      results.errors.push(`${r.medewerkerNaam} ${r.datum}: ${err.message}`);
    }
  }
  return results;
}

export async function batchImportFlexPlanning(records) {
  const results = { success: 0, errors: [] };
  for (const r of records) {
    try {
      await addDoc(collection(db, 'flexPlanning'), {
        ...r,
        createdAt: serverTimestamp(),
      });
      results.success++;
    } catch (err) {
      results.errors.push(`${r.flexkrachtNaam} ${r.datum}: ${err.message}`);
    }
  }
  return results;
}
